/* himedi-summary-next-step.js — /pages/checkup-summary next-step nudge (2026-09-17, revised 2026-09-18).
 *
 * Why: last week 110 of 239 visitors pressed the booking app's Add to Cart and never pressed
 * Checkout. On phones the "Your time is held" box (#cfCommit) is revealed below the fold and the
 * header badge still reads 0, so the page looks like nothing happened.
 *
 * What this does, and nothing else:
 *  1. Watches #cfCommit's `hidden` attribute (the flow JS clears it once the booking is in the
 *     cart). On reveal: fetch /cart.js, paint item_count into every [data-cart-count] and unhide
 *     them and [data-checkout-link]; on desktop (>760px) scroll the box to centre after a beat.
 *  2. On phones (<=760px) shows a fixed bottom bar with both actions stacked — "Checkout · $TOTAL"
 *     above "Add another checkup" (Gabe, 2026-09-18: equal weight, Checkout above for reach) — for
 *     the whole time the box is revealed. The CSS hides the box's own button row while the bar is
 *     up. The bar is never built before the box is revealed and hides again if the box is hidden.
 *     The body's bottom padding is set from the bar's measured height so nothing is covered.
 *  3. Each bar button forwards its click to the box's own button[data-commit="…"] (still in the
 *     DOM, just display:none), so the flow JS's commitCheckup runs unchanged for both paths. Each
 *     mirrors its counterpart's disabled state and label ("Starting checkout…", "Saving this checkup…").
 *
 * Never touches cart state. Reads /cart.js only. Does not edit himedi-checkup-flow.js.
 */
(function () {
  'use strict';
  var box = document.getElementById('cfCommit');
  if (!box || !('MutationObserver' in window)) return;

  var MOBILE = '(max-width: 760px)';
  var isMobile = function () { return window.matchMedia(MOBILE).matches; };
  var bar = null, totalEl = null, mirrors = [];
  var revealed = !box.hidden, total = null, refreshTimers = [];

  function money(cents) {
    var n = (Number(cents) || 0) / 100;
    var frac = Math.abs(n - Math.round(n)) < 0.005 ? 0 : 2;
    try { return '$' + n.toLocaleString('en-US', { minimumFractionDigits: frac, maximumFractionDigits: frac }); }
    catch (_) { return '$' + n.toFixed(frac); }
  }

  function readCart() {
    return fetch('/cart.js?_=' + Date.now(), { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function paintBadge(cart) {
    if (!cart) return;
    var n = Number(cart.item_count) || 0;
    if (typeof window.himediPaintCart === 'function') { try { window.himediPaintCart(n); } catch (_) {} }
    var badges = document.querySelectorAll('[data-cart-count]');
    for (var i = 0; i < badges.length; i++) { badges[i].textContent = n > 99 ? '99+' : String(n); if (n > 0) badges[i].removeAttribute('hidden'); }
    var links = document.querySelectorAll('[data-checkout-link]');
    for (var j = 0; j < links.length; j++) { if (n > 0) links[j].removeAttribute('hidden'); }
  }

  function paintTotal(cart) {
    if (!cart) return;
    total = Number(cart.total_price) || 0;
    if (totalEl) totalEl.textContent = total > 0 ? '· ' + money(total) : '';
  }

  function refresh() { return readCart().then(function (c) { paintBadge(c); paintTotal(c); }); }

  // The flow JS reconciles focus areas and add-ons into the cart for 1–3 s after the booking
  // appears, so one read at reveal time is usually short. Read again as it settles.
  function scheduleRefreshes() {
    refreshTimers.forEach(clearTimeout); refreshTimers = [];
    [0, 2500, 6000].forEach(function (ms) { refreshTimers.push(setTimeout(refresh, ms)); });
  }

  function boxBtn(mode) { return box.querySelector('button[data-commit="' + mode + '"]'); }

  // One bar button per box button. `label` is the resting text; `withTotal` appends the cart total.
  function makeButton(mode, label, cls, withTotal) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = cls;
    b.setAttribute('data-next-step', mode);
    var m = { mode: mode, el: b, label: label, withTotal: withTotal };
    setResting(m);
    b.addEventListener('click', function () {
      var real = boxBtn(mode);
      if (!real || real.disabled) return;
      // dispatchEvent reaches a display:none element; the flow JS's listener runs commitCheckup.
      real.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    });
    var real = boxBtn(mode);
    if (real) {
      new MutationObserver(function () { mirror(m); }).observe(real, { attributes: true, attributeFilter: ['disabled'], childList: true, characterData: true, subtree: true });
    }
    mirrors.push(m);
    return b;
  }

  function setResting(m) {
    m.el.textContent = m.label;
    if (m.withTotal) {
      totalEl = document.createElement('span'); totalEl.className = 'cf-next-total'; m.el.appendChild(totalEl);
      paintTotal({ total_price: total || 0 });
    }
  }

  // Mirror the real button's state so "Starting checkout…" / "Saving this checkup…" and disabled show here too.
  function mirror(m) {
    var real = boxBtn(m.mode);
    if (!real) return;
    m.el.disabled = !!real.disabled;
    if (real.disabled) { m.el.textContent = real.textContent; if (m.withTotal) totalEl = null; }
    else if (!m.withTotal || !totalEl) setResting(m);
  }

  // Clones the section-rendered <ul class="cf-assure"> (refund / date-change / tax lines, editable in
  // the section settings) so the pinned bar and the box show the same policy-backed wording from one
  // source. Absent entirely if the section predates these settings — the bar just has no lines then.
  function buildAssure() {
    var src = document.getElementById('cfAssure');
    if (!src) return null;
    var clone = src.cloneNode(true);
    clone.removeAttribute('id');
    // 2026-10-02: the bar shows one short line; the box keeps the full policy wording. Only the
    // default wording is shortened, so an edited setting still shows as written.
    var SHORT = {
      'Full refund if you cancel 7+ days before': 'Full refund up to 7 days before',
      'Change your date free, up to 12 months': 'Free date changes',
      'No tax added at checkout': 'No tax'
    };
    Array.prototype.forEach.call(clone.querySelectorAll('li span'), function (s) {
      var t = s.textContent.trim();
      if (SHORT[t]) s.textContent = SHORT[t];
    });
    clone.classList.add('cf-assure--line');
    return clone;
  }

  function buildBar() {
    if (bar) return bar;
    bar = document.createElement('div');
    bar.className = 'cf-next';
    bar.id = 'cfNext';
    bar.setAttribute('hidden', '');
    var assure = buildAssure();
    if (assure) bar.appendChild(assure);
    var co = boxBtn('checkout');
    bar.appendChild(makeButton('checkout', (co && co.textContent.trim()) || 'Checkout', 'btn', true));
    // 2026-10-02 (Gabe: "a lot going on"): the bar pins Checkout only. "Add another checkup" stays in
    // the box as its own button (CSS), so the bar is half the height it was.
    document.body.appendChild(bar);
    return bar;
  }

  // Bottom padding = the bar's rendered height (it already includes the safe-area inset), so the
  // page can scroll past it and nothing in the box is ever covered.
  function padBody() {
    if (bar && !bar.hasAttribute('hidden')) document.body.style.paddingBottom = bar.offsetHeight + 'px';
    else document.body.style.paddingBottom = '';
  }

  // The bar is up exactly when: the box is revealed and the viewport is a phone.
  function setBar() {
    if (!bar) return;
    var want = revealed && isMobile();
    if (want && bar.hasAttribute('hidden')) { bar.removeAttribute('hidden'); document.body.classList.add('cf-next-on'); refresh(); }
    else if (!want && !bar.hasAttribute('hidden')) { bar.setAttribute('hidden', ''); document.body.classList.remove('cf-next-on'); }
    padBody();
  }

  function onReveal() {
    revealed = true;
    scheduleRefreshes();
    if (isMobile()) { buildBar(); setBar(); }
    else { setTimeout(function () { try { box.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) { box.scrollIntoView(); } }, 400); }
  }

  function onHide() {
    revealed = false;
    refreshTimers.forEach(clearTimeout); refreshTimers = [];
    setBar();
  }

  new MutationObserver(function () {
    var nowHidden = box.hasAttribute('hidden');
    if (!nowHidden && !revealed) onReveal();
    else if (nowHidden && revealed) onHide();
  }).observe(box, { attributes: true, attributeFilter: ['hidden'] });

  // Viewport crossing 760px after reveal: build lazily, or drop the bar on desktop. Re-measure on resize.
  try { window.matchMedia(MOBILE).addEventListener('change', function () { if (revealed && isMobile()) buildBar(); setBar(); }); } catch (_) {}
  window.addEventListener('resize', padBody);

  // Already revealed at script time (e.g. cached page with the booking present).
  if (revealed) onReveal();

  // 2026-09-25: the summary card can take an add-on off a booked checkup; it calls this so the badge and
  // "Checkout · $TOTAL" are read from the cart again instead of showing the old total.
  window.HimediNextStep = { refresh: refresh };
})();

/* Quick book (2026-10-01, Gabe): the clinic offers one slot a day, so picking a date is the
 * choice. When the booking app's time pane shows exactly one slot, our "Add to Cart" button
 * appears under the calendar; pressing it opens the app's own pop-up off screen (CSS:
 * body.cf-quick-busy) and presses the app's own Add to Cart, so the cart line, its properties and
 * the flow JS's group seal are exactly what a manual booking makes. The button lives OUTSIDE the
 * app's React tree so a re-render can never fight it.
 * Fallback: if the pop-up's button has not appeared within 6s, or the pop-up is still open 8s
 * after the press (the app shows its own error there), the pop-up is shown as normal.
 * Also: on arrival, move off a pre-selected closed date to the first open one; after a date tap,
 * scroll the button into view if it landed below the screen edge.
 * Untouched: two or more slots, no slots, no JS — the app behaves exactly as before. */
(function () {
  'use strict';
  var host = document.querySelector('.cf-app-slot');
  if (!host || !('MutationObserver' in window)) return;
  var EMBED = '[data-servicify="inline-calendar-embed"]';
  var ATC = '[data-servicify="servicify-add-to-cart-button"]';
  var btn = document.createElement('button');
  btn.type = 'button'; btn.className = 'btn cf-quick-add'; btn.hidden = true; btn.textContent = 'Add to Cart';
  var err = document.createElement('p');
  err.className = 'cf-quick-err'; err.hidden = true;
  err.textContent = 'That didn’t go through. Tap the time above to book it instead.';
  host.appendChild(btn); host.appendChild(err);
  /* 2026-10-14 (Gabe: "fix all of these"): a date can look open and still have no time -- full, or its one
     7:00 slot is already inside the app's 3-day lead time (the first date in the calendar, every morning).
     The app then says "No timeslots available". Offer the next date that has a time, one tap away. */
  var full = document.createElement('button');
  full.type = 'button'; full.className = 'btn ghost cf-next-open';
  full.style.cssText = 'width:100%;margin:6px 0 4px;display:none'; // .btn sets display, so [hidden] alone would not hide it
  function showFull(on) { full.style.display = on ? '' : 'none'; }
  full.textContent = 'Show the next open date';
  host.appendChild(full);
  var seeking = false;
  full.addEventListener('click', function () {
    seeking = true; arrived = false; steps = 0; waitingOn = null; showFull(false);
    firstOpenDate();
  });
  var slot = null, busy = false;

  // The slot rows are the unlabelled children of the time pane's list; the skeleton and the
  // "No timeslots" box both carry data-servicify labels, so they never count.
  function slots() {
    var pane = document.querySelector(EMBED + ' [data-servicify="calendar-time-picker"] .servicify-calendar-day');
    var list = pane && pane.lastElementChild;
    if (!list) return [];
    return Array.prototype.filter.call(list.children, function (n) {
      return !n.hasAttribute('data-servicify') && /\d:\d\d/.test(n.textContent);
    });
  }

  // The app opens on a date with no slots (a closed day, or today, which it shows as open but
  // cannot book), so the first screen says "No timeslots available". On arrival only, step to
  // the next open date until one has its slot: at most 6 steps, and never after a tap.
  var arrived = false, tapped = false, steps = 0, waitingOn = null;
  function firstOpenDate() {
    if (arrived) return;
    if (slots().length) {
      arrived = true;
      if (seeking) { seeking = false; setTimeout(function () { var r = btn.getBoundingClientRect(); if (r.bottom > window.innerHeight - 24) window.scrollBy({ top: r.bottom - window.innerHeight + 24, behavior: 'smooth' }); }, 50); }
      return;
    }
    if (!document.querySelector(EMBED + ' [data-servicify="no-timeslots"]')) return; // still loading
    var sel = document.querySelector(EMBED + ' .DayPicker-Day--selected');
    if (waitingOn && sel !== waitingOn) return; // our last step has not rendered yet
    var days = Array.prototype.slice.call(document.querySelectorAll(EMBED + ' .DayPicker-Day[aria-disabled="false"]:not(.DayPicker-Day--outside)'));
    var next = days.filter(function (d) { return !sel || (sel.compareDocumentPosition(d) & Node.DOCUMENT_POSITION_FOLLOWING); })[0];
    if (!next && seeking && steps < 6) { // asked for the next open date: carry on into the next month
      var nav = document.querySelector(EMBED + ' .DayPicker-NavButton--next');
      if (nav && !nav.classList.contains('DayPicker-NavButton--interactionDisabled')) {
        steps++; nav.click();
        setTimeout(function () { var d = document.querySelector(EMBED + ' .DayPicker-Day[aria-disabled="false"]:not(.DayPicker-Day--outside)'); if (d) { waitingOn = d; d.click(); } }, 600);
        return;
      }
    }
    if (!next || ++steps > 6) { arrived = true; seeking = false; return; }
    waitingOn = next;
    next.click();
  }
  // A tapped date's button can land just below the screen edge; bring it into view, only after a tap.
  host.addEventListener('click', function (e) {
    if (e.isTrusted && e.target.closest && e.target.closest('.DayPicker-Day')) { tapped = true; arrived = true; }
  }, true);

  function sync() {
    firstOpenDate();
    if (arrived) document.body.classList.add('cf-cal-ready'); // lifts the card's first-paint hold (CSS)
    var s = slots();
    var one = s.length === 1 ? s[0] : null;
    if (slot && slot !== one) slot.classList.remove('cf-quick-slot');
    slot = one;
    if (one) one.classList.add('cf-quick-slot');
    document.body.classList.toggle('cf-quick-on', !!one);
    if (!busy) btn.hidden = !one;
    showFull(!!(arrived && !seeking && !busy && !s.length && document.querySelector(EMBED + ' [data-servicify="no-timeslots"]')));
    if (!one) err.hidden = true;
    if (one && tapped) {
      tapped = false;
      var r = btn.getBoundingClientRect();
      if (r.bottom > window.innerHeight - 24) window.scrollBy({ top: r.bottom - window.innerHeight + 24, behavior: 'smooth' });
    }
  }

  function waitFor(test, ms) {
    return new Promise(function (resolve) {
      var t0 = Date.now();
      (function tick() {
        var v = test();
        if (v || Date.now() - t0 > ms) return resolve(v || null);
        setTimeout(tick, 100);
      })();
    });
  }

  function finish(ok) {
    busy = false;
    host.style.minHeight = '';
    btn.disabled = false; btn.textContent = 'Add to Cart';
    document.body.classList.remove('cf-quick-busy');
    err.hidden = ok;
    sync();
  }

  btn.addEventListener('click', function () {
    if (busy || !slot) return;
    busy = true; err.hidden = true;
    btn.disabled = true; btn.textContent = 'Adding…';
    // Hold the slot's height while the app swaps its calendar out, so the card never collapses
    // and jumps before the booked state replaces it (CSS dims the calendar meanwhile).
    host.style.minHeight = host.offsetHeight + 'px';
    document.body.classList.add('cf-quick-busy');
    slot.click();
    waitFor(function () { return document.querySelector(ATC); }, 6000).then(function (atc) {
      if (!atc) return finish(false);
      atc.click();
      return waitFor(function () { return !document.querySelector('.ReactModal__Content'); }, 8000).then(function (closed) {
        if (!closed) return finish(false);
        // Keep "Adding…" up (and the held box out of sight, CSS) until the booked state is painted in
        // one step by himedi-summary-edit.js, so nothing half-updated ever shows. Bounded at 6s.
        return waitFor(function () { return document.body.classList.contains('cf-booked'); }, 6000).then(function () {
          finish(true);
          var box = document.getElementById('cfCommit');
          if (box && !box.hidden) box.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        });
      });
    });
  });

  sync();
  new MutationObserver(sync).observe(host, { childList: true, subtree: true, characterData: true });
})();
